import { useState, useEffect, useMemo } from 'react';
import { adminService } from '../../services';
import { Button, LoadingSpinner, Badge } from '../common';
import './UserList.css';

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [actionError, setActionError] = useState({});
  const [filterStatus, setFilterStatus] = useState('all');

  const stats = useMemo(() => {
    const total = users.length;
    const pending = users.filter(u => u.status === 'pending').length;
    const approved = users.filter(u => u.status === 'approved' || u.status === 'active').length;
    const rejected = users.filter(u => u.status === 'rejected').length;
    const admins = users.filter(u => u.isAdmin).length;
    return { total, pending, approved, rejected, admins };
  }, [users]);

  const filteredUsers = useMemo(() => {
    if (filterStatus === 'admin') return users.filter(u => u.isAdmin);
    if (filterStatus !== 'all') return users.filter(u => u.status === filterStatus);
    return users;
  }, [users, filterStatus]);

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.getAllUsers();
      if (response.success) {
        setUsers(response.data || []);
      } else {
        setError(response.message || '사용자 목록을 불러올 수 없습니다.');
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setError('사용자 목록을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit'
    });
  };

  const handleStatusChange = async (userId, newStatus) => {
    try {
      setActionLoading(prev => ({ ...prev, [`status-${userId}`]: true }));
      setActionError(prev => ({ ...prev, [`status-${userId}`]: null }));
      const response = await adminService.updateUserStatus(userId, newStatus);
      if (response.success) {
        setUsers(prev => prev.map(u =>
          u.userId === userId ? { ...u, status: newStatus, updatedAt: new Date().toISOString() } : u
        ));
      } else {
        setActionError(prev => ({ ...prev, [`status-${userId}`]: response.message || '상태 변경에 실패했습니다.' }));
      }
    } catch (err) {
      setActionError(prev => ({ ...prev, [`status-${userId}`]: '상태 변경 중 오류가 발생했습니다.' }));
    } finally {
      setActionLoading(prev => ({ ...prev, [`status-${userId}`]: false }));
    }
  };

  const handleAdminToggle = async (userId, isAdmin) => {
    try {
      setActionLoading(prev => ({ ...prev, [`admin-${userId}`]: true }));
      setActionError(prev => ({ ...prev, [`admin-${userId}`]: null }));
      const response = await adminService.updateUserAdminStatus(userId, isAdmin);
      if (response.success) {
        setUsers(prev => prev.map(u =>
          u.userId === userId ? { ...u, isAdmin: response.data.isAdmin, updatedAt: response.data.updatedAt } : u
        ));
      } else {
        setActionError(prev => ({ ...prev, [`admin-${userId}`]: response.error?.message || '관리자 권한 변경에 실패했습니다.' }));
      }
    } catch (err) {
      setActionError(prev => ({ ...prev, [`admin-${userId}`]: '관리자 권한 변경 중 오류가 발생했습니다.' }));
    } finally {
      setActionLoading(prev => ({ ...prev, [`admin-${userId}`]: false }));
    }
  };

  if (loading) {
    return (
      <div className="loading-state">
        <LoadingSpinner size="md" variant="primary" text="사용자 정보를 불러오고 있어요" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard">
        <div className="error-state">
          <div className="error-content">
            <div className="error-icon">😵</div>
            <h3>앗, 문제가 발생했어요</h3>
            <p>{error}</p>
            <Button onClick={fetchUsers} className="retry-button">🔄 다시 시도</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <main className="page-container">
      <div className="filter-tabs">
        <button className={`tab ${filterStatus === 'all' ? 'active' : ''}`} onClick={() => setFilterStatus('all')}>
          전체 ({stats.total})
        </button>
        <button className={`tab ${filterStatus === 'pending' ? 'active' : ''}`} onClick={() => setFilterStatus('pending')}>
          ⏳ 대기중 ({stats.pending})
        </button>
        <button className={`tab ${filterStatus === 'approved' ? 'active' : ''}`} onClick={() => setFilterStatus('approved')}>
          ✅ 활성 ({stats.approved})
        </button>
        {stats.rejected > 0 && (
          <button className={`tab ${filterStatus === 'rejected' ? 'active' : ''}`} onClick={() => setFilterStatus('rejected')}>
            ❌ 거부 ({stats.rejected})
          </button>
        )}
        {stats.admins > 0 && (
          <button className={`tab ${filterStatus === 'admin' ? 'active' : ''}`} onClick={() => setFilterStatus('admin')}>
            👑 관리자 ({stats.admins})
          </button>
        )}
      </div>

      {filteredUsers.length === 0 ? (
        <div className="empty-state">
          {users.length === 0 ? (
            <>
              <div className="empty-icon">👥</div>
              <h3>아직 등록된 사용자가 없어요</h3>
              <p>사용자가 가입하면 여기에 표시됩니다</p>
            </>
          ) : (
            <>
              <div className="empty-icon">🔍</div>
              <h3>필터 결과가 없어요</h3>
              <p>다른 필터를 시도해보세요</p>
              <Button className="reset-filters" onClick={() => setFilterStatus('all')}>필터 초기화</Button>
            </>
          )}
        </div>
      ) : (
        <div className="users-list">
          {filteredUsers.map((user) => (
            <div key={user.userId} className={`user-row ${user.status}`}>
              <div className="user-info">
                <div className="user-main">
                  <span className="user-name">{user.username}</span>
                  {user.isAdmin && <Badge variant="warning" size="sm">👑 관리자</Badge>}
                </div>
                <div className="user-sub">{formatDate(user.createdAt)} 가입</div>
              </div>

              <div className="user-actions">
                {user.status === 'pending' && (
                  <>
                    <Button variant="primary" size="sm" className="approve"
                      onClick={() => handleStatusChange(user.userId, 'approved')}
                      loading={actionLoading[`status-${user.userId}`]}
                      disabled={actionLoading[`status-${user.userId}`]}>
                      {actionLoading[`status-${user.userId}`] ? '처리중...' : '✓ 승인'}
                    </Button>
                    <Button variant="secondary" size="sm" className="reject"
                      onClick={() => handleStatusChange(user.userId, 'rejected')}
                      loading={actionLoading[`status-${user.userId}`]}
                      disabled={actionLoading[`status-${user.userId}`]}>
                      {actionLoading[`status-${user.userId}`] ? '처리중...' : '✕ 거부'}
                    </Button>
                  </>
                )}

                {(user.status === 'approved' || user.status === 'active') && (
                  <>
                    <Button variant="secondary" size="sm" className="toggle-admin"
                      onClick={() => handleAdminToggle(user.userId, !user.isAdmin)}
                      loading={actionLoading[`admin-${user.userId}`]}
                      disabled={actionLoading[`admin-${user.userId}`]}>
                      {actionLoading[`admin-${user.userId}`] ? '처리중...' : (user.isAdmin ? '👑 해제' : '👑 부여')}
                    </Button>
                    {user.status === 'approved' && (
                      <Button variant="secondary" size="sm" className="reject secondary"
                        onClick={() => handleStatusChange(user.userId, 'rejected')}
                        loading={actionLoading[`status-${user.userId}`]}
                        disabled={actionLoading[`status-${user.userId}`]}>
                        거부
                      </Button>
                    )}
                  </>
                )}

                {user.status === 'rejected' && (
                  <Button variant="primary" size="sm" className="approve"
                    onClick={() => handleStatusChange(user.userId, 'approved')}
                    loading={actionLoading[`status-${user.userId}`]}
                    disabled={actionLoading[`status-${user.userId}`]}>
                    {actionLoading[`status-${user.userId}`] ? '처리중...' : '↻ 재승인'}
                  </Button>
                )}
              </div>

              {(actionError[`status-${user.userId}`] || actionError[`admin-${user.userId}`]) && (
                <div className="error-text">
                  {actionError[`status-${user.userId}`] || actionError[`admin-${user.userId}`]}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </main>
  );
};

export default UserList;
